/* This is a high order version of the RouteAware mixin for use with ES6 class syntax
*	 example use -
*	 export default RouteAware(MyComponent)
*/

import React from 'react'
import T from 'prop-types'
import getBaseClass from './getBaseClass'

const RouteAware = WrappedComponent => {
  const BaseClass = getBaseClass(WrappedComponent)

  return class RouteAwareWrapped extends BaseClass {
    static displayName = `RouteAware(${WrappedComponent.displayName ||
      'Component'})`

    static propTypes = {
      routeSubscriber: T.func, //required only on topmost component mixing it in
      getRef: T.func // TODO remove and use regular refs when React addresses the issues at the end of this https://facebook.github.io/react/docs/higher-order-components.html
    }

    static contextTypes = {
      routeAwareParentInfo: T.object
    }

    static childContextTypes = {
      routeAwareParentInfo: T.object,
      childMethods: T.object
    }

    constructor(props) {
      super(props)
      this.state = {
        route: null
      }
    }

    getChildContext() {
      return {
        routeAwareParentInfo: this._routeAwareChildInfo
      }
    }

    UNSAFE_componentWillMount() {
      // If this component is a descendant of a routeAware ancestor, then we can't have both
      // listening to the same route change event directly because that results in bad things like
      // the child having setState called even though it's going to be unmounted. Instead,
      // we'll just let the child respond to the componentWillReceiveProps that results from the
      // ancestor's handler.
      // See https://www.tildedave.com/2014/11/15/introduction-to-contexts-in-react-js.html for
      // info about how we're using the undocumented child contexts feature here.
      const parentInfo = this.context.routeAwareParentInfo
      this._routeAwareChildInfo = { route: null } //single instance we can mutate to avoid order-of-execution issues with getChildContext
      if (parentInfo) {
        if (parentInfo.route) {
          this._onRouteChange(parentInfo.route)
        }
      } else {
        const subscriber = this.props.routeSubscriber
        if (!subscriber) {
          throw new Error(
            "The topmost component using the routeAware mixin must be given a 'routeSubscriber' function in the props."
          )
        }
        this._routeUnsubscriber = subscriber(this._onRouteChange)
      }
    }

    componentWillUnmount() {
      if (this._routeUnsubscriber) {
        this._routeUnsubscriber(this._onRouteChange)
      }
    }

    UNSAFE_componentWillReceiveProps() {
      const parentInfo = this.context.routeAwareParentInfo
      if (parentInfo && parentInfo.route !== this.state.currentRoute) {
        this._onRouteChange(parentInfo.route)
      }
    }

    _onRouteChange = newRoute => {
      this._routeAwareChildInfo.route = newRoute
      this.setState({
        route: newRoute
      })
    }
    render() {
      const { route } = this.state
      const refProps = {}
      refProps[
        WrappedComponent.propTypes && WrappedComponent.propTypes.getRef
          ? 'getRef'
          : 'ref'
      ] = this.props.getRef
      return <WrappedComponent {...refProps} {...this.props} route={route} />
    }
  };
}

export default RouteAware
